/**
 * Higher everyday living fee (HELF) and bed reservation agreement inputs for
 * one residential fee day (B4c, migration 20435000003262).
 *
 * This is the calculator step that turns stored agreements into the day
 * calculator's `helfCents` and `bedReservation` inputs. Storage keeps the
 * agreed amount and its frequency; conversion to a daily amount happens here
 * only (G3). `internal.residential_helf_day_from_inputs` in 3262 mirrors this
 * file exactly; generate refuses a day whose tags differ from the SQL result.
 *
 * Rules applied, in order, for each agreement that covers the day:
 * 1. A single-occasion (ad hoc) agreement counts only on its one day
 *    (Rules r 284-2, r 284-13(1)).
 * 2. A recorded suspension zeroes the day (r 284-11(13)): extended hospital
 *    leave (mandatory, (13)(a)) or other leave by agreement ((13)(b)).
 * 3. A hospital-leave day zeroes the day even without a recorded suspension.
 *    The meaning of "extended hospital leave" is UNVERIFIED (clause artefact
 *    C7, path γ), so every hospital-leave day is treated as suspended. This
 *    cannot over-charge.
 * 4. An agreement recorded before the structured service fields existed
 *    (3260 rows) is held until it is corrected (G1/G2).
 * 5. r 284-15 indexation replaces the agreed amount on each 1 July from
 *    1 July 2026 that falls after the agreement date and on or before the day:
 *    factor = reference March-quarter CPI / base March-quarter CPI, to 3
 *    decimal places (4th decimal 5 or more rounds up), applied only when the
 *    factor is greater than 1, result rounded to the nearest whole dollar
 *    (50 cents up). Base = the highest March quarter before the reference
 *    quarter, not earlier than March 2025. The reference quarter
 *    is the last March quarter before the indexation day (verified in HELF-T). A missing CPI value
 *    holds the agreement for the day (HELD_CPI_MISSING); nothing is guessed.
 * 6. Conversion to a daily amount, integer cents, half up: DAILY as is,
 *    WEEKLY / 7, FORTNIGHTLY / 14, MONTHLY x 12 / 365 (provider convention,
 *    open question), SINGLE_OCCASION the whole amount on its day.
 * 7. GST: only an agreement whose treatment a tax adviser confirmed as
 *    GST_FREE is charged. UNDETERMINED (and TAXABLE, which has no GST
 *    calculation yet) is held (TAX_REVIEW_REQUIRED, clause artefact
 *    gst-treatment-advisory). Held cents are reported, never charged.
 *
 * Bed reservation: only a BED_RESERVATION day reads the agreement (Act s 282).
 * The day calculator caps the agreed amount at the r 282-5 / r 285A-20
 * maximum. An agreement whose GST is not GST_FREE is held: the calculator
 * gets no agreement for the day.
 *
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-helf-and-bed-reservation-agreements-primary-source-verification.md
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-gst-treatment-advisory.md
 */

import type { ResidentialBedReservationInput } from './day-calculator';
import type { ResidentialDayState } from './day-states';
import { assertResidentialFeeDate } from './rates';

export const HELF_CHARGE_FREQUENCIES = [
  'DAILY',
  'WEEKLY',
  'FORTNIGHTLY',
  'MONTHLY',
  'SINGLE_OCCASION',
] as const;
export type HelfChargeFrequency = (typeof HELF_CHARGE_FREQUENCIES)[number];

export const HELF_AGREEMENT_KINDS = ['STANDING', 'AD_HOC'] as const;
export type HelfAgreementKind = (typeof HELF_AGREEMENT_KINDS)[number];

export const HELF_SUSPENSION_KINDS = ['EXTENDED_HOSPITAL_LEAVE', 'AGREED_OTHER_LEAVE'] as const;
export type HelfSuspensionKind = (typeof HELF_SUSPENSION_KINDS)[number];

/** r 284-15(1): the first indexation day. */
export const HELF_FIRST_INDEXATION_YEAR = 2026;
/** r 284-15(3): the base quarter is not earlier than the March quarter 2025. */
export const HELF_CPI_BASE_FLOOR_YEAR = 2025;

export const HELF_DAY_LINE_STATUSES = [
  'CHARGED',
  'HELD_GST_UNDETERMINED',
  'HELD_TAXABLE_NOT_SUPPORTED',
  'HELD_CPI_MISSING',
  'HELD_AGREEMENT_INCOMPLETE',
  'HELD_REGIME_MISMATCH',
  'HELD_5A_FACTS_MISSING',
  'REFUSED_TRANSITIONAL_SERVICE_5A',
  'HELD_OLD_FEE_OVERLAP',
  'SUSPENDED_EXTENDED_HOSPITAL_LEAVE',
  'SUSPENDED_AGREED_LEAVE',
  'SUSPENDED_HOSPITAL_LEAVE',
] as const;
export type HelfDayLineStatus = (typeof HELF_DAY_LINE_STATUSES)[number];

export const BED_RESERVATION_DAY_STATUSES = [
  'CHARGED',
  'HELD_GST_UNDETERMINED',
  'HELD_TAXABLE_NOT_SUPPORTED',
  'NO_AGREEMENT',
] as const;
export type BedReservationDayStatus = (typeof BED_RESERVATION_DAY_STATUSES)[number];

export type HelfRegime = 'S284' | 'TRANSITIONAL_R285A_25';
export const HELF_PRE_TRANSITION_SERVICE_BASES = [
  'NOT_DELIVERED_BEFORE_TRANSITION',
  'FREE_UNDER_RESIDENT_AGREEMENT',
  'UNDER_EXTRA_SERVICE_AGREEMENT',
  'UNDER_S56_1E_AGREEMENT',
  'UNKNOWN',
] as const;
export type HelfPreTransitionServiceBasis = (typeof HELF_PRE_TRANSITION_SERVICE_BASES)[number];

/** Evidence of fees actually charged elsewhere; this never bills an old fee. */
export interface HelfOldFeeChargeInput {
  serviceCode: string;
  chargedFrom: string;
  chargedTo: string;
}

export interface HelfTransitionFactsInput {
  residentAgreementInEffect: boolean | null;
  extraServiceAgreementInForce: boolean | null;
  s56ServiceCodes: readonly string[];
}

export interface HelfAgreementDayInput {
  id: string;
  /** ACTIVE, ENDED or SUPERSEDED. SUPERSEDED rows never count. */
  status: string;
  agreementKind: HelfAgreementKind | null;
  chargeAmountCents: number;
  chargeFrequency: HelfChargeFrequency | null;
  agreedOn: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  gstTreatment: string;
  /** service_code, service_type and fee_basis are all recorded. */
  structured: boolean;
  helfRegime: HelfRegime | null;
  serviceCode?: string | null;
  preTransitionServiceBasis?: HelfPreTransitionServiceBasis | null;
}

export interface HelfSuspensionDayInput {
  agreementId: string;
  suspensionKind: HelfSuspensionKind;
  startsOn: string;
  /** Null while the suspension is open. */
  endsOn: string | null;
}

export interface BedReservationAgreementDayInput {
  id: string;
  status: string;
  agreedDailyCents: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  gstTreatment: string;
}

/**
 * All Groups CPI (weighted average of the 8 capital cities), March quarter,
 * keyed by year ("2025"), as an exact decimal string with up to 4 places.
 * A missing year is unknown, not zero.
 */
export type HelfCpiMarchIndex = Readonly<Record<string, string>>;

export interface HelfDayLine {
  agreementId: string;
  status: HelfDayLineStatus;
  /** The day's amount for CHARGED and HELD_GST_UNDETERMINED; 0 for every other status. */
  dailyCents: number;
}

export interface HelfDayResult {
  date: string;
  /** Pass to the day calculator as `helfCents`. */
  chargeCents: number;
  /** Amount held out of billing (GST or incomplete agreement). Not charged. */
  heldCents: number;
  /** Agreements charged on this day, sorted. */
  chargedAgreementIds: string[];
  /** One line per agreement that covers the day, sorted by agreement id. */
  lines: HelfDayLine[];
}

export interface BedReservationDayResult {
  /** Pass to the day calculator as `bedReservation`. */
  input: ResidentialBedReservationInput | null;
  /** Set only when the agreement is charged. */
  agreementId: string | null;
  /** Null on a day that is not a bed reservation day. */
  status: BedReservationDayStatus | null;
}

function assertCents(name: string, value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer number of cents`);
  }
}

/** Integer division rounding half up, for non-negative operands. */
function divideHalfUp(numerator: number, denominator: number): number {
  return Math.floor((2 * numerator + denominator) / (2 * denominator));
}

/** CPI index string to integer ten-thousandths, or null when absent or malformed. */
export function parseHelfCpiIndex(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d+)(?:\.(\d{1,4}))?$/.exec(value.trim());
  if (!match) return null;
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? '').padEnd(4, '0'));
  const result = whole * 10000 + fraction;
  return result > 0 && Number.isSafeInteger(result) ? result : null;
}

/**
 * r 284-15(3)-(4): the indexation factor for 1 July of `year`, in thousandths
 * (1.023 is 1023). Null when any March quarter it needs is missing.
 */
export function helfIndexationFactorThousandths(
  cpi: HelfCpiMarchIndex,
  year: number,
): number | null {
  if (!Number.isInteger(year) || year < HELF_FIRST_INDEXATION_YEAR) {
    throw new RangeError('HELF indexation starts on 1 July 2026');
  }
  const reference = parseHelfCpiIndex(cpi[String(year)]);
  if (reference === null) return null;
  let base: number | null = null;
  for (let prior = HELF_CPI_BASE_FLOOR_YEAR; prior < year; prior += 1) {
    const value = parseHelfCpiIndex(cpi[String(prior)]);
    if (value === null) return null;
    if (base === null || value > base) base = value;
  }
  if (base === null) return null;
  return divideHalfUp(reference * 1000, base);
}

export type HelfIndexedAmount =
  | { ok: true; amountCents: number; indexedYears: number[] }
  | { ok: false; missingYear: number };

/**
 * r 284-15(1)-(2), (6): the agreed amount in force on `date`, after every
 * indexation day that falls after `agreedOn` and on or before `date`.
 */
export function helfIndexedAmountCents(
  amountCents: number,
  agreedOn: string,
  date: string,
  cpi: HelfCpiMarchIndex,
): HelfIndexedAmount {
  assertCents('amountCents', amountCents);
  assertResidentialFeeDate(agreedOn);
  assertResidentialFeeDate(date);
  let current = amountCents;
  const indexedYears: number[] = [];
  const lastYear = Number(date.slice(0, 4));
  for (let year = HELF_FIRST_INDEXATION_YEAR; year <= lastYear; year += 1) {
    const indexationDay = `${year}-07-01`;
    if (indexationDay <= agreedOn || indexationDay > date) continue;
    const factor = helfIndexationFactorThousandths(cpi, year);
    if (factor === null) return { ok: false, missingYear: year };
    if (factor > 1000) {
      // eslint-disable-next-line statura/no-floating-point-money -- integer cents x integer thousandths; exact below 2^53
      current = divideHalfUp(current * factor, 100000) * 100;
      indexedYears.push(year);
    }
  }
  return { ok: true, amountCents: current, indexedYears };
}

/** G3: the agreed amount for its frequency, as a daily amount in cents (half up). */
export function helfDailyCents(amountCents: number, frequency: HelfChargeFrequency): number {
  assertCents('amountCents', amountCents);
  switch (frequency) {
    case 'DAILY':
    case 'SINGLE_OCCASION':
      return amountCents;
    case 'WEEKLY':
      return divideHalfUp(amountCents, 7);
    case 'FORTNIGHTLY':
      return divideHalfUp(amountCents, 14);
    case 'MONTHLY':
      return divideHalfUp(amountCents * 12, 365);
    default:
      throw new RangeError(`Unknown HELF charge frequency: ${String(frequency)}`);
  }
}

function covers(from: string, to: string | null, date: string): boolean {
  return from <= date && (to === null || date <= to);
}

export function resolveHelfDay(input: {
  date: string;
  dayState: ResidentialDayState;
  agreements: readonly HelfAgreementDayInput[];
  suspensions?: readonly HelfSuspensionDayInput[];
  cpi?: HelfCpiMarchIndex;
  /** The arrangement family covering this billing day; unknown classes hold the fee. */
  arrangementFamily: string | null;
  transitionFacts?: HelfTransitionFactsInput | null;
  oldFeeCharges?: readonly HelfOldFeeChargeInput[];
}): HelfDayResult {
  assertResidentialFeeDate(input.date);
  const cpi = input.cpi ?? {};
  const suspensions = input.suspensions ?? [];
  const lines: HelfDayLine[] = [];
  for (const agreement of input.agreements) {
    if (agreement.status === 'SUPERSEDED') continue;
    if (!covers(agreement.effectiveFrom, agreement.effectiveTo, input.date)) continue;
    if (agreement.chargeFrequency === 'SINGLE_OCCASION' && agreement.effectiveFrom !== input.date) {
      continue;
    }
    const suspension = suspensions.find(
      (row) => row.agreementId === agreement.id && covers(row.startsOn, row.endsOn, input.date),
    );
    if (suspension) {
      lines.push({
        agreementId: agreement.id,
        status:
          suspension.suspensionKind === 'EXTENDED_HOSPITAL_LEAVE'
            ? 'SUSPENDED_EXTENDED_HOSPITAL_LEAVE'
            : 'SUSPENDED_AGREED_LEAVE',
        dailyCents: 0,
      });
      continue;
    }
    if (input.dayState === 'LEAVE_HOSPITAL') {
      lines.push({ agreementId: agreement.id, status: 'SUSPENDED_HOSPITAL_LEAVE', dailyCents: 0 });
      continue;
    }
    let transitionStatus: HelfDayLineStatus | null = null;
    {
      const expected =
        input.arrangementFamily === 'NOVEMBER_1_2025'
          ? 'S284'
          : input.arrangementFamily === 'JULY_1_2014'
            ? 'TRANSITIONAL_R285A_25'
            : null;
      if (expected === null || agreement.helfRegime !== expected) {
        transitionStatus = 'HELD_REGIME_MISMATCH';
      } else if (
        (input.oldFeeCharges ?? []).some(
          (fee) =>
            fee.serviceCode === agreement.serviceCode &&
            covers(fee.chargedFrom, fee.chargedTo, input.date),
        )
      ) {
        transitionStatus = 'HELD_OLD_FEE_OVERLAP';
      } else if (agreement.helfRegime === 'TRANSITIONAL_R285A_25') {
        if (input.date >= '2026-11-01') {
          const basis = agreement.preTransitionServiceBasis;
          const facts = input.transitionFacts;
          if (
            !basis ||
            basis === 'UNKNOWN' ||
            !facts ||
            facts.extraServiceAgreementInForce === null ||
            facts.residentAgreementInEffect === null
          ) {
            transitionStatus = 'HELD_5A_FACTS_MISSING';
          } else if (
            basis === 'FREE_UNDER_RESIDENT_AGREEMENT' &&
            facts.residentAgreementInEffect &&
            !facts.extraServiceAgreementInForce &&
            !facts.s56ServiceCodes.includes(agreement.serviceCode ?? '')
          ) {
            transitionStatus = 'REFUSED_TRANSITIONAL_SERVICE_5A';
          }
        }
      }
    }
    if (transitionStatus) {
      lines.push({ agreementId: agreement.id, status: transitionStatus, dailyCents: 0 });
      continue;
    }
    if (!agreement.structured || agreement.chargeFrequency === null) {
      lines.push({
        agreementId: agreement.id,
        status: 'HELD_AGREEMENT_INCOMPLETE',
        dailyCents: 0,
      });
      continue;
    }
    const indexed = helfIndexedAmountCents(
      agreement.chargeAmountCents,
      agreement.agreedOn,
      input.date,
      cpi,
    );
    if (!indexed.ok) {
      lines.push({ agreementId: agreement.id, status: 'HELD_CPI_MISSING', dailyCents: 0 });
      continue;
    }
    const dailyCents = helfDailyCents(indexed.amountCents, agreement.chargeFrequency);
    lines.push({
      agreementId: agreement.id,
      status:
        agreement.gstTreatment === 'GST_FREE'
          ? 'CHARGED'
          : agreement.gstTreatment === 'TAXABLE'
            ? 'HELD_TAXABLE_NOT_SUPPORTED'
            : 'HELD_GST_UNDETERMINED',
      dailyCents,
    });
  }
  lines.sort((left, right) => (left.agreementId < right.agreementId ? -1 : 1));
  let chargeCents = 0;
  let heldCents = 0;
  const chargedAgreementIds: string[] = [];
  for (const line of lines) {
    if (line.status === 'CHARGED') {
      chargeCents += line.dailyCents;
      chargedAgreementIds.push(line.agreementId);
    } else if (
      line.status === 'HELD_GST_UNDETERMINED' ||
      line.status === 'HELD_TAXABLE_NOT_SUPPORTED'
    ) {
      heldCents += line.dailyCents;
    }
  }
  return { date: input.date, chargeCents, heldCents, chargedAgreementIds, lines };
}

export function resolveBedReservationDay(input: {
  date: string;
  dayState: ResidentialDayState;
  agreements: readonly BedReservationAgreementDayInput[];
}): BedReservationDayResult {
  assertResidentialFeeDate(input.date);
  if (input.dayState !== 'BED_RESERVATION') {
    return { input: null, agreementId: null, status: null };
  }
  const agreement = input.agreements.find(
    (row) => row.status !== 'SUPERSEDED' && covers(row.effectiveFrom, row.effectiveTo, input.date),
  );
  if (!agreement) return { input: null, agreementId: null, status: 'NO_AGREEMENT' };
  assertCents('agreedDailyCents', agreement.agreedDailyCents);
  if (agreement.gstTreatment !== 'GST_FREE') {
    return {
      input: null,
      agreementId: null,
      status:
        agreement.gstTreatment === 'TAXABLE'
          ? 'HELD_TAXABLE_NOT_SUPPORTED'
          : 'HELD_GST_UNDETERMINED',
    };
  }
  return {
    input: {
      agreedDailyCents: agreement.agreedDailyCents,
      agreementId: agreement.id,
      gstTreatment: 'GST_FREE_RESIDENTIAL_CARE',
    },
    agreementId: agreement.id,
    status: 'CHARGED',
  };
}

/** The breakdown-day tags generate stores and SQL re-derives (snake case, canonical order). */
export interface ResidentialAgreementDayTags {
  helf_agreement_ids: string[];
  helf_held_cents: number;
  helf_lines: Array<{ agreement_id: string; status: HelfDayLineStatus; daily_cents: number }>;
  bed_reservation_agreement_id: string | null;
  bed_reservation_status: BedReservationDayStatus | null;
}

export function residentialAgreementDayTags(
  helf: HelfDayResult,
  bed: BedReservationDayResult,
): ResidentialAgreementDayTags {
  return {
    helf_agreement_ids: [...helf.chargedAgreementIds],
    helf_held_cents: helf.heldCents,
    helf_lines: helf.lines.map((line) => ({
      agreement_id: line.agreementId,
      status: line.status,
      daily_cents: line.dailyCents,
    })),
    bed_reservation_agreement_id: bed.agreementId,
    bed_reservation_status: bed.status,
  };
}
